/* INSTRUCTOR TUTORIAL — "Your console in five minutes"
 * (DESIGN-tutorial-system.md §3). Eight steps, one chapter, running on the
 * class console.
 *
 * The design doc wrote this copy against the seeded demo class (MGMT 480, three
 * part-played students), and the first cut hard-coded that name — so a real
 * instructor opening their own brand-new class was told they were looking at
 * someone else's demo, and was pointed at a leaderboard and a Review drilldown
 * that do not exist until somebody plays a quarter. Steps whose truth depends
 * on the class therefore take the live ctx and rewrite themselves; {className}
 * is resolved by the host. */
import type { TutCtx, TutSeries } from './types';

const TAB_LABELS: Record<string, string> = {
  leaderboard: '🏆 Leaderboard',
  students: '👥 Students',
  teams: '🧑‍🤝‍🧑 Teams',
  grading: '🎯 Grading',
  settings: '⚙️ Settings',
};

/** Students who have resolved at least one quarter — i.e. is there data yet. */
const played = (c: TutCtx) => c.playedCount ?? 0;
/** Students enrolled, played or not. */
const enrolled = (c: TutCtx) => c.studentCount ?? 0;
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const instructorSeries: TutSeries = {
  id: 'instructor',
  title: 'Your console in five minutes',
  tabAnchor: (screen) => 'ic-tab-' + screen,
  tabHint: (screen) => `Open the ${TAB_LABELS[screen] ?? screen} tab to continue.`,
  chapters: [
    {
      id: 'console',
      title: 'Your console in five minutes',
      tasks: [
        {
          id: 'ic-drilldown',
          label: "Open a student's Review → drilldown",
          done: (c) => c.drilldownOpen,
          // Nothing to drill into until someone has played, and an untickable
          // box is worse than no box.
          when: (c) => played(c) > 0,
        },
      ],
      steps: [
        {
          id: 'ic-welcome',
          title: 'This is your class.',
          body: (c) => {
            if (played(c) > 0) {
              return `${plural(played(c), 'student has', 'students have')} played at least one ` +
                'quarter in {className}, so the tabs below are populated with real data. ' +
                'The tour takes about five minutes.';
            }
            if (enrolled(c) > 0) {
              return `{className} has ${plural(enrolled(c), 'student', 'students')} enrolled and ` +
                'nobody has finished a quarter yet, so the tables are still empty. The tour ' +
                'shows you where everything lives — the numbers fill in as they play.';
            }
            return 'No students in {className} yet, so there is nothing to rank or grade ' +
              'so far. The tour takes about five minutes and walks the whole console, so you ' +
              'know what to expect the day your class starts playing.';
          },
        },
        {
          id: 'ic-joincode',
          anchor: 'ic-joincode',
          title: 'Students join with this code.',
          body:
            'Put it in your syllabus. Students sign up, enter the code, and start playing — ' +
            'no rosters to upload, no IT tickets. (Locked out student? The 🔑 Reset button ' +
            'issues a temporary password on the spot.)',
        },
        {
          id: 'ic-leaderboard',
          anchor: 'ic-tab-leaderboard',
          title: 'The leaderboard is a balanced scorecard, not a profit contest.',
          body: (c) =>
            'Eight dimensions roll into the composite — so a focused cost strategy and a ' +
            'premium strategy can both win. ' +
            (played(c) > 0
              ? "Click any student's Review → to see why they're ranked where they are."
              : 'Students appear here after their first quarter, each with a Review → that ' +
                'explains the rank.'),
        },
        {
          id: 'ic-review',
          screen: 'leaderboard',
          anchor: 'ic-review',
          title: 'The review drilldown is your grade defense.',
          body: (c) =>
            (played(c) > 0 ? '' : 'Once a student has played, their row gets a Review → button. ') +
            'Every quarter: what they decided, what happened, and what they said they were ' +
            'doing (their journal). Radar for the scorecard, charts for the financials. This ' +
            'is also your assurance-of-learning evidence.',
          done: (c) => c.drilldownOpen,
        },
        {
          id: 'ic-students',
          anchor: 'ic-tab-students',
          title: 'Spot drift by week 3.',
          body:
            'Quarters played, quiz completion, journal count. A student with 1 quarter in ' +
            'week 5 needs a nudge long before finals.',
        },
        {
          id: 'ic-grading',
          anchor: 'ic-tab-grading',
          title: 'Grades compute themselves; you stay in charge.',
          body:
            'Scorecard / quiz / journal at the weights you set (⚙️ Settings). Override any ' +
            'grade — overrides are flagged, never silent. Export CSV for your gradebook.',
        },
        {
          id: 'ic-settings',
          anchor: 'ic-tab-settings',
          title: 'Course setup lives here.',
          body:
            'Game length (8 quarters fits a semester), grade weights, and the assignment ' +
            'brief your students see on day one.',
        },
        {
          id: 'ic-done',
          title: "That's the whole job.",
          body: (c) =>
            (enrolled(c) === 0
              ? 'Put the join code in your syllabus and you are running — this console fills ' +
                'itself in as students play. '
              : 'From here it runs itself while your students play. ') +
            'The course kit has a week-by-week plan mapping each decision screen to the topic ' +
            'you teach that week. Questions — education@capitalismlab.com reaches a human.',
        },
      ],
    },
  ],
};
